import { gql } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
} from '@apollo/orbit';
import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { UpgradeButton } from 'src/components/upgradeButton/UpgradeButton';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useShowSelfServeScaleUI } from 'src/lib/featureFlags';
import {
  PersistedQueriesConfigCardServiceFragment,
  PersistedQueriesConfigCardVariantFragment,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers';

import { graphPersistedQueriesRouteConfig } from '../../routes';

export const persistedQueriesConfigCardServiceFragment = gql`
  fragment PersistedQueriesConfigCardServiceFragment on Service {
    id
    persistedQueryLists {
      id
    }
  }
`;

export const persistedQueriesConfigCardVariantFragment = gql`
  fragment PersistedQueriesConfigCardVariantFragment on GraphVariant {
    id
    persistedQueryList {
      id
      name
    }
  }
`;

interface Props {
  settingsLevel: 'graph' | 'variant';
  graph: PersistedQueriesConfigCardServiceFragment;
}

type GraphOrVariantLevelProps = Props &
  (
    | {
        settingsLevel: 'graph';
        graphVariant?: never;
      }
    | {
        settingsLevel: 'variant';
        graphVariant: PersistedQueriesConfigCardVariantFragment;
      }
  );

export const PersistedQueriesConfigCard = ({
  settingsLevel,
  graph,
  graphVariant,
}: GraphOrVariantLevelProps) => {
  const showSelfServeScaleUI = useShowSelfServeScaleUI();
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Persisted queries
          <Tooltip
            interactive
            className="w-auto"
            placement="right"
            label={
              <p className="w-44 whitespace-normal p-2">
                Use persisted queries to enforce a safelist on your variants or
                to manage a list of short query IDs that can be used in place of
                full freeform GraphQL in requests. Learn more about what you can
                do{' '}
                <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                  here
                </Link>
                .
              </p>
            }
          >
            <IconInfo className="text-icon-primary" />
          </Tooltip>
        </CardTitle>
        <CardDescription>
          {settingsLevel === 'graph' && graph ? (
            isMinLengthArray(1, graph.persistedQueryLists) ? (
              <>
                {graph.persistedQueryLists.length} persisted query list
                {graph.persistedQueryLists.length !== 1 ? 's' : ''} configured.
              </>
            ) : (
              'No persisted query lists configured.'
            )
          ) : settingsLevel === 'variant' && graphVariant ? (
            graphVariant.persistedQueryList ? (
              <>
                Linked to persisted query list:{' '}
                <em>{graphVariant.persistedQueryList.name}</em>
              </>
            ) : (
              <>No persisted query lists are linked to this variant.</>
            )
          ) : (
            <ErrorMessage />
          )}
        </CardDescription>
        {currentPlan.currentPlan?.persistedQueries ? (
          <PermissionWarning
            graphId={graph.id}
            graphPermissions="canQueryPersistedQueryLists"
            childRenderer={(hasPermission) => (
              <Button
                isDisabled={!hasPermission}
                variant="secondary"
                as={ReactRouterLink}
                to={graphPersistedQueriesRouteConfig.location({
                  graphId: graph.id,
                })}
              >
                Configure persisted queries
              </Button>
            )}
          />
        ) : currentAccountId ? (
          <UpgradeButton accountId={currentAccountId} variant="secondary">
            {showSelfServeScaleUI ? 'Upgrade' : 'Request upgrade'}
          </UpgradeButton>
        ) : null}
      </CardHeader>
    </Card>
  );
};
